import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Customer } from '@inyova/models';
import { getStorageItemsByPrefix, storeItemsInLocalStorage } from '@inyova/utils';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';

import { Store } from '@ngrx/store';
import * as AppRootActions from '../app.actions';

import { AuthService } from '@shared/services/auth.service';
import { LanguageService } from '@shared/services/language.service';
import { ToastNotificationService } from '@shared/services/toast-notification.service';
import { TrackingService } from '@shared/services/tracking.service';
import { anonymiseEmail, decodeRedirectURL, getRedirectionDomainInfo } from '@shared/utils';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false
})
export class LoginComponent implements OnInit {
  @ViewChild('verifyCodeInput') inputElement: ElementRef;
  loginForm: FormGroup;
  loading = false;
  error = false;
  blocked = false;
  left_attempts = 0;
  passwordVisible = false;
  token: string;
  showRegisterButton = false;

  // 2FA
  twoFAActivated: boolean;
  phone: string;
  hiddenPhone: string;
  inputCode = '';
  autoLogout = false;
  redirectURL = '/';
  invalidCall = false;
  deDisabled = false;

  get emailControl(): AbstractControl | null {
    return this.loginForm.get('email');
  }

  get passwordControl(): AbstractControl | null {
    return this.loginForm.get('password');
  }

  get locale() {
    return this.languageService.getRedirectLanguage();
  }

  constructor(
    private authService: AuthService,
    private fb: FormBuilder,
    private router: Router,
    private http: HttpClient,
    private languageService: LanguageService,
    private trackingService: TrackingService,
    private route: ActivatedRoute,
    private store: Store,
    private toastNotificationService: ToastNotificationService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.autoLogout = Boolean(this.route.snapshot.queryParamMap.get('autoLogout'));
    if (this.autoLogout) {
      setTimeout(() => {
        this.toastNotificationService.show('warning', this.translateService.instant('login_screen.autoLoggedOut.p'), {
          permanent: true
        });
      }, 500);
    }
    this.showRegisterButton = !window.location.hostname.includes('inyova.de');

    const redirectParam: string | null = this.route.snapshot.queryParamMap.get('redirect');

    this.redirectURL = redirectParam ? decodeRedirectURL(redirectParam) : '/';

    if (this.autoLogout && !redirectParam) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.router.navigate(['login']);
    }

    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });

    const referrerCookie = document.cookie.match(RegExp('(?:^|;\\s*)' + 'referrer' + '=([^;]*)'));
    if (referrerCookie === null) {
      document.cookie = `referrer=${document.referrer}`;
    }

    this.trackingService.setSTIFromURL();
  }

  goRegister(): void {
    switch (this.languageService.getRedirectLanguage()) {
      case 'de':
        void this.router.navigate(['/profil-erstellen']);
        break;
      case 'en':
        void this.router.navigate(['/create-profile']);
        break;
      case 'fr':
        void this.router.navigate(['/creer-profil']);
        break;
      default:
        void this.router.navigate(['/profil-erstellen']);
        break;
    }
  }

  signIn(): void {
    this.loading = true;
    this.error = false;
    this.deDisabled = false;

    if (!this.loginForm.invalid) {
      // get and set greenness survey localstorage items
      const items = getStorageItemsByPrefix('hideGreennessSurvey_');
      localStorage.clear();
      storeItemsInLocalStorage(items);
      const { email, password } = this.loginForm.value;
      this.authService
        .signIn({
          email,
          password
        })
        .subscribe(
          ({ body }) => {
            if (body.status === 'success') {
              if (!body.data) {
                // 2FA activated
                const response = body as unknown as { phone: string; authentication_token: string; success: string };

                this.phone = response.phone;
                this.hiddenPhone = this.phone.slice(0, 3) + this.phone.slice(4).replace(/.(?=..)/g, '*');
                this.twoFAActivated = true;
                this.error = false;
                this.token = response.authentication_token;

                setTimeout(() => {
                  if (this.inputElement) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    this.inputElement.nativeElement.focus();
                  }
                }, 1200);
              } else {
                // Normal Login
                const customer = body.data;

                this.loading = false;
                this.error = false;
                this.switchAppBasedOnAppLocation(customer);
                this.store.dispatch(AppRootActions.fetchCustomerSuccess({ customer, selectActiveAccount: true }));
                this.authService.updateLoggedIn(true);
                this.trackingService.trackLastTouchPoint();

                if (!customer.email_confirmed) {
                  void this.router.navigate(['/next/verify-email']);
                  return;
                }

                void this.router.navigateByUrl(this.redirectURL);
              }
            } else {
              this.loading = false;
              this.error = true;
            }
          },
          (error: { status: number; url: string; error: { code: string[]; errors: string[]; status: boolean; left_attempts?: number } }) => {
            // TODO: Remove after Login issue investigation
            if (!error?.status || error?.status !== 401) {
              Sentry.captureException(new Error('Expection: Invalid customer login response'), {
                extra: {
                  ...error,
                  message: error.error.errors[0],
                  hint: anonymiseEmail(email as string)
                },
                tags: {
                  section: 'login'
                }
              });
            }

            this.loading = false;
            this.error = true;

            if (error && error.error && error.error?.code?.includes('de_disabled')) {
              this.deDisabled = true;
              return;
            }

            if (error && error.error && error.error.errors && error.error.errors[0].includes('locked')) {
              this.blocked = true;
              return;
            }

            this.invalidCall = !error?.status;
            if (error?.error?.left_attempts !== undefined) {
              this.left_attempts = error.error.left_attempts + 1;
            }
          }
        );
    } else {
      this.loading = false;
      this.error = false;
    }
  }

  googleLogin(token: string) {
    this.loading = true;

    this.authService.loginWithGoogle(token).subscribe(
      ({ status, data }) => {
        if (status === 'success') {
          this.switchAppBasedOnAppLocation(data);

          this.error = false;
          this.loading = false;
          this.store.dispatch(AppRootActions.fetchCustomerSuccess({ customer: data, selectActiveAccount: true }));
          this.authService.updateLoggedIn(true);
          this.trackingService.trackLastTouchPoint();
          this.trackingService.trackActivity('Logged in Google');
          void this.router.navigateByUrl(this.redirectURL);
        } else {
          this.loading = false;
          this.error = true;
        }
      },
      () => {
        this.loading = false;
        this.error = true;
      }
    );
  }

  // 2FA functions
  sendCode(): void {
    this.authService.validate2FACode(this.inputCode, this.token).subscribe(
      (response) => {
        const { body } = response;
        if (body.status === 'success') {
          this.switchAppBasedOnAppLocation(body.data);
          this.loading = false;
          this.error = false;
          this.store.dispatch(AppRootActions.fetchCustomerSuccess({ customer: body.data, selectActiveAccount: true }));
          this.authService.updateLoggedIn(true);
          this.trackingService.trackLastTouchPoint();
          void this.router.navigateByUrl(this.redirectURL);
          this.trackingService.trackActivity('Logged in (2fa)');
        } else {
          this.loading = false;
          this.error = true;
          this.inputCode = '';
        }
      },
      (error) => {
        this.loading = false;
        this.error = true;
        this.inputCode = '';
        // eslint-disable-next-line no-console
        console.log('2FA - AuthService', error);
      }
    );
  }

  inputUpdate(event: string): void {
    if (event && event.length === 6) {
      this.sendCode();
    }
    // Reset error
    if (event.length > 0) {
      this.error = false;
    }
  }

  resendCode(): void {
    this.inputCode = '';
    this.error = false;
    (this.inputElement.nativeElement as HTMLInputElement).focus();
    this.signIn();
  }

  getArticle(): string {
    const articleUrl = {
      en: 'https://help.inyova.ch/hc/en-us/articles/360017220140-How-do-I-enable-two-factor-authentication-',
      fr: 'https://help.inyova.ch/hc/fr/articles/360017220140-Comment-puis-je-activer-l-authentification-%C3%A0-deux-facteurs-',
      de: 'https://help.inyova.ch/hc/de/articles/360017220140-Wie-aktiviere-ich-die-Zwei-Faktor-Authentifizierung-'
    };
    return articleUrl[this.languageService.getRedirectLanguage()] as string;
  }

  private switchAppBasedOnAppLocation(customer: Customer): void {
    const { origin } = window.location;

    const { finalDomain, appLocation } = getRedirectionDomainInfo(customer.app_location, customer.inyova_pro);
    const targetUrl = `https://${finalDomain}.inyova.${appLocation}`;

    if (origin.includes('.cloud') || origin.includes('localhost') || origin.includes(targetUrl)) {
      return;
    }

    const urlQuery = `/?customer_token=${customer.authentication_token}&customer_email=${customer.email}`;
    window.open(`${targetUrl}${urlQuery}`, '_self');
  }

  passVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
    document.getElementById('password').focus();
  }
}
